import React, { useEffect, useState } from "react";
import { Box, Drawer, Button, IconButton, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { IconX } from "@tabler/icons-react";

import { updateUser } from "../../../services/userService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { getInitials } from "./candidateRow";
import tokens from "../../theme/tokens";

const { color, font, layout, radius, shadow } = tokens;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { padding: "12px 13px", fontSize: 13.5 },
};

const Label = ({ children }) => (
  <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
    {children}
  </Box>
);

/**
 * Your own profile. Replaces the old EditUserForm dialog, which the v1 rail was still
 * opening — an obvious seam, since it dropped an old-styled MUI dialog into the console.
 *
 * The component holds camelCase state and converts to the API's snake_case on save,
 * which is the same contract the old dialog had; getUserById returns camelCase.
 */
const ProfilePanel = ({ open, onClose, user, onSaved }) => {
  const dispatch = useDispatch();

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setErrors({});
    setForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
    });
  }, [open, user]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: "" }));
  };

  const handleSave = async () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "Required";
    if (!form.lastName.trim()) next.lastName = "Required";
    if (!EMAIL.test(form.email)) next.email = "Enter a valid email address.";
    // The old form required E.164; keep that rule so we don't send the API something
    // it will reject, but allow an empty phone.
    if (form.phone && !form.phone.startsWith("+")) {
      next.phone = "Include the country code, e.g. +91…";
    }
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      await updateUser(user.userId, {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
      });
      onSaved?.(form);
      dispatch(
        showAppAlert({ show: true, alertType: "success", alertMessage: "Profile updated." })
      );
      onClose();
    } catch (e) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: e.message || "Failed to update your profile.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: layout.detailPanel,
          maxWidth: "100%",
          boxShadow: shadow.panel,
          borderLeft: `1px solid ${color.border}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: 2.75, pb: 2 }}>
        <Box sx={{ flex: 1, font: `600 15px ${font.sans}`, color: color.text }}>Your profile</Box>
        <IconButton size="small" onClick={onClose}>
          <IconX size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "grid",
              placeItems: "center",
              borderRadius: `${radius.fileRow}px`,
              bgcolor: color.indigoTint,
              color: color.indigo,
              font: `600 15px ${font.sans}`,
            }}
          >
            {getInitials(`${form.firstName} ${form.lastName}`.trim()) || "?"}
          </Box>
          <Box sx={{ font: `400 11.5px ${font.mono}`, color: color.textMuted }}>
            {form.email || "No email"}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
          <Box>
            <Label>First name</Label>
            <TextField
              fullWidth
              size="small"
              value={form.firstName}
              onChange={set("firstName")}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
              sx={field}
            />
          </Box>
          <Box>
            <Label>Last name</Label>
            <TextField
              fullWidth
              size="small"
              value={form.lastName}
              onChange={set("lastName")}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
              sx={field}
            />
          </Box>
        </Box>

        <Label>Email</Label>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={form.email}
          onChange={set("email")}
          error={Boolean(errors.email)}
          helperText={errors.email}
          sx={{ ...field, mb: 2 }}
        />

        <Label>Phone</Label>
        <TextField
          fullWidth
          size="small"
          placeholder="+91 98765 43210"
          value={form.phone}
          onChange={set("phone")}
          error={Boolean(errors.phone)}
          helperText={errors.phone}
          sx={field}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.75,
          py: 2,
          borderTop: `1px solid ${color.border}`,
          bgcolor: color.bg,
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ProfilePanel;
